<template>
  <div>
    <b-form-group class="mb-0">
      <b-input-group class="input-group-alternative input-group-merge">
        <div class="input-group-prepend">
          <span class="input-group-text"><i class="fas fa-search"></i></span>
        </div>
        <b-form-input v-model="searchKeyword" placeholder="Search" type="text" :disabled="false"></b-form-input>
      </b-input-group>
    </b-form-group>

    <div v-loading="isFetching" class="checkbox-list mt-4">
      <template v-if="filteredOptionsList.length">
        <b-form-checkbox v-if="!searchKeyword" @change="onSelectAll" :checked="isAllSelected" class="text-capitalize"
          :disabled="disabled">All Reports</b-form-checkbox>

        <b-form-checkbox v-for="optionItem in filteredOptionsList" v-model="innerValue" :value="optionItem.value"
          :key="`reports-checkbox-list-${optionItem.value}`"
          :disabled="disabled || disabledReports.includes(optionItem.value)">
          <span class="d-flex align-items-center">
            {{ optionItem.text }}

            <template v-if="currentUser.is_superuser && !disabled">
              <a v-if="innerValue.includes(optionItem.value)"
                @click.stop.prevent="$emit('update:defaultReport', optionItem.value)" :class="{
          'text-primary font-weight-bold': defaultReport == optionItem.value,
          'text-muted': defaultReport != optionItem.value,
        }" class="small ml-auto" href>Set Default</a>
            </template>
          </span>
        </b-form-checkbox>
      </template>
      <p v-else class="small">No reports available.</p>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from "@vue/composition-api";
import Fuse from "fuse.js";

import { useAuth } from "@composables/auth";
import Reports from "@services/Reports";

export default defineComponent({
  name: "ReportsCheckboxList",
  emits: ["input", "update:reportsFetched", "update:defaultReport"],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    assigneeId: [String, Number],
    assigneeType: {
      type: String,
      default: "Employee",
      validator: (value) => {
        return ["Employee", "Corporate Admin"].includes(value);
      },
    },
    defaultReport: Number,
    disabledReports: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { currentUser } = useAuth();

    const isFetching = ref(false);
    const reportsList = ref([]);
    const searchKeyword = ref("");

    const innerValue = computed({
      get: () => props.value,
      set: (newValue) => {
        if (props.disabled) return;

        if (newValue.length > 0 && !newValue.includes(props.defaultReport)) {
          emit("update:defaultReport", newValue[0]);
        }

        emit("input", newValue);
      },
    });

    const optionsList = computed(() => {
      if (!reportsList.value || !Array.isArray(reportsList.value)) {
        return [];
      }

      return reportsList.value
        .filter(item => item && (item.report_id || item.id))
        .map((item) => ({
          value: item.report_id || item.id,
          text: currentUser.value.is_superuser ? item.name : item.display_name || item.name,
        }));
    });

    const filteredOptionsList = computed(() => {
      if (!searchKeyword.value) {
        return optionsList.value;
      }

      try {
        const fuse = new Fuse(optionsList.value, {
          keys: ["text"],
          useExtendedSearch: true,
          threshold: 0.3
        });

        const results = fuse.search(searchKeyword.value);
        return results.map((item) => item.item).filter(item => item);
      } catch (error) {
        console.error('Search error:', error);
        return optionsList.value;
      }
    });

    const isAllSelected = computed(() => {
      return filteredOptionsList.value.length > 0 &&
        filteredOptionsList.value.length === props.value.length;
    });

    const onSelectAll = () => {
      if (props.disabled) return;

      const emitValue = isAllSelected.value
        ? props.disabledReports || []
        : filteredOptionsList.value.map((item) => item.value);

      emit("input", emitValue);
    };

    const getReports = async () => {
      isFetching.value = true;

      try {
        const resp = await Reports.getReports(
          props.assigneeId,
          props.assigneeType,
          {
            page_size: 1000,
          },
          !!!props.assigneeId
        );

        reportsList.value = resp.data.data.results || resp.data.data.data || [];

        if (!Array.isArray(reportsList.value)) {
          reportsList.value = [];
        }

        isFetching.value = false;

        emit("update:reportsFetched", optionsList.value);
      } catch (error) {
        console.error('Error fetching reports:', error);
        reportsList.value = [];
        isFetching.value = false;
      }
    };

    onMounted(getReports);

    return {
      currentUser,
      innerValue,
      searchKeyword,
      isFetching,
      isAllSelected,
      optionsList,
      filteredOptionsList,
      onSelectAll,
    };
  },
});
</script>

<style lang="scss">
.checkbox-list {
  max-height: 190px;
  overflow-y: auto;

  .custom-control {
    margin-bottom: 0.5rem;
  }
}

.custom-checkbox.disabled .custom-control-label,
.custom-control-input:disabled~.custom-control-label {
  opacity: 0.85 !important;
  color: #333 !important;
  cursor: not-allowed;
}

.custom-control-input:disabled~.custom-control-label::before {
  background-color: #f8f9fa !important;
  border-color: #6c757d !important;
  opacity: 0.9 !important;
}

.custom-control-input:disabled:checked~.custom-control-label::before {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

.input-group-alternative input:disabled {
  opacity: 1;
  cursor: text;
  background-color: #fff !important;
}

.checkbox-list .text-muted {
  color: #6c757d !important;
}
</style>